<template>
  <div class="related-to-page baker-nav">
    <ul>
      <li :class="{ active: route.path === '/bakers/overview' }">
        <router-link to="/bakers/overview">Overview</router-link>
      </li>
      <li v-for="(trait) in traits" :key="trait" :class="{ active: route.params.trait === slugify(trait) }">
        <router-link :to="`/detail/${slugify(trait)}`"> {{ trait }}</router-link>
      </li>
    </ul>
  </div>
</template>
<script>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { getBakersdetailResult } from "@/services/bakers";

export default {
  name: "TraitViewer",

  setup() {
    const store = useStore();
    const route = useRoute();
    const loadingStatus = ref("loading");
    const introduction = ref(null);

    const slugify = (text) =>
      text.toLowerCase().replace(/\s+/g, "-");
    let initialTrait = 0;

    if (route.path === "/bakers/overview") {
      initialTrait = null;
    }

    const activeTrait = ref(initialTrait);
    const activeSubtrait = ref(0);
    const segments = 10;
    const currentRoute = computed(() => route.path);

    const isActive = (path) => {
      return currentRoute.value === path;
    };

    const barcode = computed(() => {
      return store.getters.profile?.profile?.barcode || null;
    });

    watch(
      barcode,
      (newBarcode) => {
        if (!newBarcode) return;

        loadingStatus.value = "loading";

        getBakersdetailResult(newBarcode)
          .then((res) => {
            introduction.value = res;
            loadingStatus.value = "loaded";
          })
          .catch(() => {
            loadingStatus.value = "loadingFailed";
          });
      },
      { immediate: true }
    );

    const parsedResults = computed(() => {
      if (!introduction.value?.results) return null;
      try {
        return JSON.parse(introduction.value.results);
      } catch {
        return null;
      }
    });

    const traits = computed(() => {
      return parsedResults.value?.traits
        ? Object.keys(parsedResults.value.traits)
        : [];
    });

    const currentTrait = computed(() => {
      if (!traits.value.length) return "";
      return traits.value[activeTrait.value];
    });

    const subtraits = computed(() => {
      if (!currentTrait.value) return [];
      return Object.entries(
        parsedResults.value.traits[currentTrait.value].subtraits
      );
    });

    const currentSubtrait = computed(() => {
      return subtraits.value[activeSubtrait.value] || null;
    });

    const currentSubtraitName = computed(() => {
      return currentSubtrait.value?.[0] || "";
    });

    const currentSnps = computed(() => {
      return currentSubtrait.value?.[1]?.snps || null;
    });

    const totalScore = computed(() => {
      return currentSubtrait.value?.[1]?.total?.total_score ?? null;
    });

    const segmentStyle = (index) => {
      if (totalScore.value === null) {
        return { backgroundColor: "#EFEFEF" };
      }
      const activeSegments = Math.ceil(totalScore.value * segments);
      if (index > activeSegments) {
        return { backgroundColor: "#EFEFEF" };
      }
      if (index === activeSegments) {
        return { backgroundColor: "#F37021" };
      }
      return { backgroundColor: "#FFE2CF" };
    };

    const phenotypeClass = (phenotype) => {
      if (!phenotype) return "";
      if (phenotype === "positive") return "phenotype-positive";
      if (phenotype === "negative") return "phenotype-negative";
      if (phenotype === "intermediate") return "phenotype-intermediate";
      return "";
    };

    return {
      route,
      slugify,
      loadingStatus,
      traits,
      subtraits,
      activeTrait,
      activeSubtrait,
      currentTrait,
      currentSubtraitName,
      currentSnps,
      totalScore,
      segments,
      currentRoute,
      isActive,
      segmentStyle,
      phenotypeClass,
    };
  },
};
</script>
<style lang="scss">
.related-to-page {
  width: 100%;
  display: flex;
  flex-direction: row;
  margin: 20px 0px;
}

.result-wrapper .related-to-page ul {
  overflow: initial;
  max-width: 100%;
}

.related-to-page.baker-nav ul {
  display: flex;
  list-style: none;
  flex-direction: row;
  padding-left: 0px;
  gap: 30px;
  margin-bottom: 0px;
}

.related-to-page.baker-nav li {
  width: auto;
  list-style: none;
  padding: 0px 10px;
}

.related-to-page.baker-nav li.active {
  color: black;
  position: relative;
}

.related-to-page.baker-nav li.active::after {
  content: "";
  position: absolute;
  height: 5px;
  width: 100%;
  background-color: #E86D1E !important;
  left: 0px;
  bottom: -10px;
  border-radius: 10px;
}

.related-to-page.baker-nav li.active a {
  color: black;
  font-weight: 600 !important;
}

.related-to-page.baker-nav li:hover {
  color: black;
  position: relative;
}

.related-to-page.baker-nav li:hover::after {
  content: "";
  position: absolute;
  height: 5px;
  width: 100%;
  background-color: #E86D1E !important;
  left: 0px;
  bottom: -10px;
  border-radius: 10px;
}

.related-to-page.baker-nav li:hover a {
  color: black;
}

.result-wrapper li {
  line-height: initial;
}

.related-to-page.baker-nav a {
  color: #a7a7a7;
  text-decoration: none;
  font-weight: 400;
  padding: 0px 8px 5px !important;
  border-bottom: 4px solid transparent;
  border-radius: 2px;
  font-size: 16px;
}

.nav-item {
  all: unset;
  display: block;
  cursor: pointer;
  padding: 10px;
}

@media (max-width: 990px) {
  .result-wrapper li {
    padding: 0;
  }
}

@media (max-width: 767px) {
  .related-to-page.baker-nav {
    padding: 16px 0px;
    margin: 10px 0 15px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .related-to-page.baker-nav::-webkit-scrollbar {
    display: none;
  }

  .related-to-page.baker-nav ul {
    gap: 20px;
    padding: 0 10px;
    white-space: nowrap;
  }

  .related-to-page.baker-nav li {
    flex-shrink: 0;
    padding: 0px;
  }
}
</style>